import { useEffect, useRef, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { uploadChartAsImage } from '../../Editor/utils/UploadToServer';
import { downloadChartAsImage } from './downloadChartAsImage';
import { Button } from 'reactstrap';
import SectionModal from '../../Editor/utils/SectionModal';


const PieChart = ({ containerid, data, height = 500 }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const chartId = useMemo(() => `pie-chart-${Math.random().toString(36).substring(2, 11)}`, []);
  const [containerWidth, setContainerWidth] = useState(0);
  const [actionPush, setactionPush] = useState('');


  /**
  * function call to Push code to editor
  */
  const addToEditor = (data) => {
    console.log(data)
    uploadChartAsImage(data, containerid)
    setactionPush("")
  }



  // Handle container resizing
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      setContainerWidth(containerRef.current.clientWidth);
    };

    const resizeObserver = new ResizeObserver(entries => {
      if (!entries?.length) return;
      updateDimensions();
    });

    resizeObserver.observe(containerRef.current);
    updateDimensions();

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data || !data.length || !svgRef.current || !containerWidth) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set dimensions with responsive width
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const width = containerWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Set chart size to be 60% of the width, leaving 40% for legend
    const chartWidth = width * 0.6;
    const legendWidth = width * 0.4;
    const radius = Math.min(chartWidth, chartHeight) / 2;

    // Create root SVG with responsive viewBox
    const svg = d3.select(svgRef.current)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .attr("viewBox", `0 0 ${containerWidth} ${height}`)
      .append("g")
      .attr("transform", `translate(${containerWidth / 2}, ${height / 2})`);

    // Create a group for the pie chart, positioned on the left
    const pieGroup = svg.append("g")
      .attr("transform", `translate(${-legendWidth / 2}, 0)`);

    const colors = d3.scaleOrdinal()
      .domain(data.map(d => d.variable))
      .range([
        "#FF6B6B", "#4ECDC4", "#45B7D1",
        "#96CEB4", "#FFEEAD", "#D4A5A5",
        "#9B59B6", "#3498DB", "#E67E22"
      ]);

    // Create pie layout
    const pie = d3.pie()
      .value(d => d.percentage)
      .sort(null);

    // Create arc generator
    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(radius * 0.8);

    // Create tooltip with fixed positioning
    const tooltipId = `tooltip-${chartId}`;
    const tooltip = d3.select('body')
      .append('div')
      .attr('class', 'project-dash-chart-tooltip')
      .attr('id', tooltipId)
      .style('position', 'fixed')
      .style('display', 'none');

    // Add the pie segments
    const paths = pieGroup.selectAll("path")
      .data(pie(data))
      .join("path")
      .attr("d", arc)
      .attr("fill", d => colors(d.data.variable))
      .attr("stroke", "white")
      .style("stroke-width", "2px")
      .style("opacity", 1)
      .on("mouseover", (event, d) => {
        const target = event.currentTarget;
        d3.select(target)
          .transition()
          .duration(100)
          .style("opacity", 0.8);

        d3.select(`#${tooltipId}`)
          .style('display', 'block')
          .html(`
            <div class="tooltip-title">${d.data.variable}</div>
            <div>${parseFloat(d.data.percentage).toFixed(1)}%</div>
            <div>(${d.data.frequency} points)</div>
          `)

      })
      .on("mousemove", (event) => {
        d3.select(`#${tooltipId}`)
          .style('left', `${event.clientX + 10}px`)
          .style('top', `${event.clientY - 10}px`);
      })
      .on("mouseout", (event) => {
        const target = event.currentTarget;
        d3.select(target)
          .transition()
          .duration(100)
          .style("opacity", 1);
        d3.select(`#${tooltipId}`).style('display', 'none');
      });

    // Add internal labels (numbers) to the chart
    pieGroup.selectAll("text.value")
      .data(pie(data))
      .join("text")
      .attr("class", "value")
      .attr("transform", d => `translate(${arc.centroid(d)})`)
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .style("font-weight", "600")
      .style("font-size", `${Math.min(12, width / 50)}px`)
      .style("fill", "#ffffff")
      .text(d => `${parseFloat(d.data.percentage).toFixed(1)}%`);

    // Create a group for the legend, positioned on the right
    const legendGroup = svg.append("g")
      .attr("transform", `translate(${radius - 100}, ${-chartHeight / 2 + margin.top})`);

    // Add legend title
    legendGroup.append("text")
      .attr("x", 0)
      .attr("y", 20)
      .style("font-size", `${Math.min(14, width / 40)}px`)
      .style("font-weight", "bold")
      .text("Legend");

    // Add legend items
    const legendRectSize = 15;
    const legendSpacing = 5;
    const legendHeight = legendRectSize + legendSpacing;

    const legend = legendGroup.selectAll('.legend-item')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${40 + i * (legendHeight + 10)})`);

    // Add colored rectangle to legend
    legend.append('rect')
      .attr('width', legendRectSize)
      .attr('height', legendRectSize)
      .style('fill', d => colors(d.variable))
      .style('stroke', d => colors(d.variable))

    // Add legend text
    legend.append('text')
      .attr('x', legendRectSize + legendSpacing)
      .attr('y', legendRectSize - legendSpacing / 2)
      .style("font-size", `${Math.min(12, width / 50)}px`)
      .text(d => `${d.variable} (${d.percentage}%)`);

    // Add data points to legend
    legend.append('text')
      .attr('x', legendRectSize + legendSpacing)
      .attr('y', legendRectSize + 10)
      .style("font-size", `${Math.min(10, width / 60)}px`)
      .style("fill", "#666666")
      .text(d => d.frequency);

    // Cleanup
    return () => {
      d3.select(`#${tooltipId}`).remove();
    };
  }, [data, height, chartId, containerWidth]);

  return (
    <>
      <div className="controller no-print">
        {
          actionPush == "section" &&
          <SectionModal pushAction={addToEditor} onclose={() => setactionPush("")} />
        }


        <Button className='grid no-print' onClick={() => downloadChartAsImage(containerid)}>
          Download
        </Button>
        <Button className='grid no-print' onClick={() => setactionPush("section")}>
          Copy to Ediotor
        </Button>
      </div>

      <div ref={containerRef} id={containerid} style={{ width: '100%', height: '100%' }}>
        <svg
          
          ref={svgRef}
          width="100%"
          height={height}
          className="chart-svg"
        />
      </div>
    </>
  );
};

export default PieChart;

